import React, { memo, useContext, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import isEmpty from "lodash/isEmpty";
import HttpStatus from "@lib-shared/constants/http-status";
import { useEffectOnce } from "@lib-shared/hooks";
import { MenuArea } from "core/constants";
import { BusinessPage, ButtonText } from "core/constants";
import { AppContext } from "core/context";
import { SelfServiceContext } from "core/context/self-service.context";
import { Feature, isEnabled } from "core/features";
import { useUpdateMenu } from "core/hooks";
import { sessionManager } from "core/instances";
import { logApiError, UrlUtils } from "core/lib";
import { ShowModalProps } from "core/lib/show-modal";
import { FreshDeskService } from "core/services/fresh-desk.service";
import FreshDeskStore from "core/stores/fresh-desk.store";
import CaptureUserDetailsForm from "components/capture-user-details-form";
import { ShowModal, ModalAlert, ModalIcon, PleaseWait } from "components/common-ui";
import ListQueries from "../self-service/support/case-management/list-queries";

const SupportLayout = () => {
    const navigate = useNavigate();
    const selfServiceContext = useContext(SelfServiceContext);
    const appContext = useContext(AppContext);

    const store = selfServiceContext.stores.freshDesk;

    const dataManager = selfServiceContext.managers.tickets;

    const user = appContext?.user;

    const cancellationToken = axios.CancelToken.source();
    const [isGettingFreshDeskUser, _setIsGettingFreshDeskUser] = useState(false);
    const [showCaptureUserDetailsForm, setShowCaptureDetailsForm] = useState(false);
    const [hasFreshDeskAuthError, setHasFreshDeskAuthError] = useState(false);

    const api = new FreshDeskService();

    const goBackToDashboard = (event: any) => navigate(BusinessPage.dashboard);

    useUpdateMenu(MenuArea.queries);

    let existingDialog: HTMLDialogElement;

    const closeModal = () => {
        if (existingDialog != null) {
            ModalAlert.close(existingDialog);
        }
    }

    const setIsGettingFreshDeskUser = (value: boolean) => {
        if (value) {
            showLoadingModal();
        } else {
            closeModal();
        }

        _setIsGettingFreshDeskUser(value);
    }

    const showLoadingModal = () => {
        closeModal();

        const options: ShowModalProps = {
            appendSpinner: true,
            title: "Getting Freshdesk User",
            showClose: false,
            showButton: false
        };

        existingDialog = ShowModal(options);
    }

    const showErrorModal = (description: string) => {
        closeModal();

        setHasFreshDeskAuthError(true);

        const options = {
            body: description,
            title: "Error",
            icon: ModalIcon.error,
            showClose: false,
            buttonText: ButtonText.goBackToDashboard,
            onClose: goBackToDashboard
        };

        existingDialog = ShowModal(options);
    }

    const _getFreshdeskUserDataAsync = async (fullName: string | undefined = undefined) => {
        setIsGettingFreshDeskUser(true);

        const details = user?.accountDetails;

        if (details == null) {
            setIsGettingFreshDeskUser(false);
            return;
        }

        try {
            const contactResponse = await api.getContactAsync(fullName);

            if (contactResponse.status !== HttpStatus.ok) {
                return;
            }

            const contact = contactResponse.data;

            if (contact !== null) {
                setIsGettingFreshDeskUser(false);

                if (contact.id === -1) {
                    setShowCaptureDetailsForm(true);
                    setHasFreshDeskAuthError(false);
                    return;
                }

                if (contact.id === 0) {
                    showErrorModal("Error getting Freshdesk user")
                    return;
                }

                contact.name = contact.name.replace(/null/g, "").trim();

                store.updateContact(contact);
            }
        } catch (error: any) {
            let message = error?.message;

            logApiError(error);

            message = message.includes("Request failed") ? "Error getting Freshdesk user" : message;
            showErrorModal(message);
            return;
        }

        setIsGettingFreshDeskUser(false);
    }

    const checkFreshDeskAccessAsync = async () => {
        console.log("checkFreshDeskAccess");

        if ((store.contact && FreshDeskStore.cache.isCached) || hasFreshDeskAuthError) {
            return;
        }

        const accessToken = user?.accessToken;

        if (isEmpty(accessToken)) {
            setTimeout(async () => await checkFreshDeskAccessAsync(), 1000);
            return;
        }

        if (isGettingFreshDeskUser === true) {
            return;
        }

        const { firstName, lastName } = sessionManager.getNameOfUser();

        const fullName = `${firstName} ${lastName}`;

        await _getFreshdeskUserDataAsync(fullName);
    }

    const handleCaptureOfFullNameOfUser = async (firstName: string, lastName: string) => {
        const fullName = `${firstName} ${lastName}`;

        setShowCaptureDetailsForm(false);

        await _getFreshdeskUserDataAsync(fullName);
    }

    useEffectOnce(() => {
        const onLoad = async () => {
            appContext.updateHeader("Support");

            cancellationToken.cancel();

            UrlUtils.removeQueryParamsFromUrl();

            if (!isEnabled(Feature.support)) {
                ModalAlert.permissionsError(() => navigate(BusinessPage.dashboard));
                return;
            }

            await checkFreshDeskAccessAsync();
        }

        onLoad();
    });

    if (hasFreshDeskAuthError) {
        return null;
    }

    if (isGettingFreshDeskUser) {
        return null;
    }

    if (showCaptureUserDetailsForm || (store.contact && isEmpty(store.contact.name))) {
        return <CaptureUserDetailsForm
            isClosable={true}
            onClose={goBackToDashboard}
            onCancel={goBackToDashboard}
            onSubmit={handleCaptureOfFullNameOfUser} />;
    }

    if (!store.contact || !FreshDeskStore.cache.isCached) {
        return <PleaseWait message="Getting support details" />;
    }

    return (
        <Routes>
            <Route path="*" element={<ListQueries dataManager={dataManager} auth={store.contact} />} />
        </Routes>
    );
}

export default memo(SupportLayout);
